/**
 * CookieBanner.jsx
 * GDPR-compliant cookie consent modal with granular controls.
 * Matches ZenAnalytics dark theme.
 *
 * Tabs:   Consent | Details | About
 * Categories: Essential (always) | Functional | Analytics | Marketing
 * Buttons: Reject Non-Essential | Customize / Save Preferences | Accept All
 */

import { useState, useEffect } from 'react';

const GA_ID      = 'G-TLLMMQKWLL';
const STORAGE_KEY = 'zen_cookie_consent';

// ── GA loader ─────────────────────────────────────────────────────────────────
function loadGA() {
  if (window.__gaLoaded) return;
  window.__gaLoaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID);
}

/** Called at app startup — load GA only if user already consented. */
export function initGAIfConsented() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    // Handle legacy string format ('accepted' / 'rejected')
    if (raw === 'accepted') { loadGA(); return; }
    if (raw === 'rejected') return;
    const saved = JSON.parse(raw);
    if (saved?.analytics) loadGA();
  } catch {}
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:     '#0f172a',
  card:   '#1e293b',
  border: '#334155',
  text:   '#e2e8f0',
  muted:  '#94a3b8',
  accent: '#00d4ff',
  purple: '#7c3aed',
};

// ── Toggle switch ─────────────────────────────────────────────────────────────
function Toggle({ value, onChange, disabled }) {
  return (
    <div
      role="switch"
      aria-checked={value}
      onClick={() => !disabled && onChange(!value)}
      style={{
        width: 44, height: 24, borderRadius: 12,
        background: value ? C.accent : C.border,
        cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative',
        transition: 'background 0.2s',
        flexShrink: 0,
        opacity: disabled ? 0.55 : 1,
        userSelect: 'none',
      }}
    >
      <div style={{
        position: 'absolute',
        top: 2, left: value ? 22 : 2,
        width: 20, height: 20,
        borderRadius: '50%',
        background: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
      }} />
    </div>
  );
}

// ── Cookie categories ─────────────────────────────────────────────────────────
const CATEGORIES = [
  {
    key: 'essential',
    name: 'Essential',
    count: 4,
    always: true,
    description:
      'Required for authentication, session management, and security. These cookies keep you signed in and protect your account. They cannot be disabled — the service would not function without them.',
  },
  {
    key: 'functional',
    name: 'Functional',
    count: 6,
    always: false,
    description:
      'Remember your preferences such as demo mode settings, selected filters, chart options, and other UI state across sessions to improve your experience.',
  },
  {
    key: 'analytics',
    name: 'Analytics',
    count: 3,
    always: false,
    description:
      'Google Analytics (GA4) collects anonymised usage data — pages visited, session duration, feature usage — to help us understand how ZenAnalytics is used and where we can improve.',
  },
  {
    key: 'marketing',
    name: 'Marketing',
    count: 2,
    always: false,
    description:
      'Enables personalised advertising and retargeting across partner platforms, helping us reach racket sport players who may benefit from ZenAnalytics. No personal training data is ever shared.',
  },
];

const DEFAULT_PREFS = { functional: true, analytics: false, marketing: false };

// ── Main component ────────────────────────────────────────────────────────────
export default function CookieBanner() {
  const [visible,  setVisible]  = useState(false);
  const [tab,      setTab]      = useState('consent'); // 'consent' | 'details' | 'about'
  const [prefs,    setPrefs]    = useState(DEFAULT_PREFS);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) { setVisible(true); return; }
      if (raw === 'accepted') { loadGA(); return; }
      if (raw === 'rejected') return;
      const saved = JSON.parse(raw);
      if (saved?.analytics) loadGA();
      // If we have a saved consent record, don't show banner
    } catch {
      setVisible(true);
    }
  }, []);

  const saveConsent = (preferences) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      essential: true,
      ...preferences,
      timestamp: new Date().toISOString(),
    }));
    if (preferences.analytics) loadGA();
    setVisible(false);
  };

  const acceptAll      = () => saveConsent({ functional: true,  analytics: true,  marketing: true  });
  const rejectAll      = () => saveConsent({ functional: false, analytics: false, marketing: false });
  const saveCustom     = () => saveConsent(prefs);

  if (!visible) return null;

  const TABS = ['Consent', 'Details', 'About'];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.72)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <div style={{
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '520px',
        maxHeight: 'calc(100vh - 48px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 24px 80px rgba(0,0,0,0.85)',
        fontFamily: "'Inter', system-ui, sans-serif",
      }}>

        {/* ── Tabs ─────────────────────────────────────────────────────── */}
        <div style={{
          display: 'flex',
          borderBottom: `1px solid ${C.border}`,
          flexShrink: 0,
        }}>
          {TABS.map(t => {
            const active = tab === t.toLowerCase();
            return (
              <button key={t} onClick={() => setTab(t.toLowerCase())} style={{
                flex: 1,
                padding: '14px 0',
                background: 'none',
                border: 'none',
                borderBottom: active ? `2px solid ${C.accent}` : '2px solid transparent',
                color: active ? C.accent : C.muted,
                fontSize: '14px',
                fontWeight: active ? 700 : 400,
                cursor: 'pointer',
                transition: 'all 0.15s',
                marginBottom: -1,
              }}>{t}</button>
            );
          })}
        </div>

        {/* ── Content area ─────────────────────────────────────────────── */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px' }}>

          {/* CONSENT tab */}
          {tab === 'consent' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 28 }}>⌚</span>
                <h2 style={{ margin: 0, color: C.text, fontSize: 17, fontWeight: 700, lineHeight: 1.3 }}>
                  ZenAnalytics uses cookies
                </h2>
              </div>
              <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.75, margin: '0 0 12px' }}>
                We use cookies to keep you signed in, remember your preferences, and understand how
                ZenAnalytics is used — so we can keep making it better for our users.
              </p>
              <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.75, margin: 0 }}>
                Essential cookies are always active. You can customise optional cookies or review our{' '}
                <a href="/privacy" style={{ color: C.accent, textDecoration: 'none' }}>Privacy Policy</a>.
              </p>
            </div>
          )}

          {/* DETAILS tab */}
          {tab === 'details' && (
            <div>
              {CATEGORIES.map((cat, i) => (
                <div key={cat.key} style={{
                  borderBottom: i < CATEGORIES.length - 1 ? `1px solid ${C.border}` : 'none',
                  padding: '14px 0',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>{cat.name}</span>
                      <span style={{
                        background: C.card, border: `1px solid ${C.border}`,
                        borderRadius: 20, padding: '1px 8px',
                        color: C.muted, fontSize: 11,
                      }}>{cat.count}</span>
                      {cat.always && (
                        <span style={{ color: C.accent, fontSize: 11, fontWeight: 600 }}>Always active</span>
                      )}
                    </div>
                    <Toggle
                      value={cat.always ? true : prefs[cat.key]}
                      disabled={cat.always}
                      onChange={(v) => setPrefs(p => ({ ...p, [cat.key]: v }))}
                    />
                  </div>
                  <p style={{ color: C.muted, fontSize: 12, lineHeight: 1.65, margin: 0 }}>
                    {cat.description}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* ABOUT tab */}
          {tab === 'about' && (
            <div>
              <h3 style={{ color: C.text, fontSize: 15, fontWeight: 700, marginTop: 0, marginBottom: 14 }}>
                About cookies on ZenAnalytics
              </h3>
              <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.75, marginBottom: 12 }}>
                Cookies are small text files stored in your browser. ZenAnalytics uses them to
                authenticate your session, remember your settings, measure feature usage, and —
                with your consent — support personalised advertising.
              </p>
              <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.75, marginBottom: 12 }}>
                Your training data is never shared with advertisers or third-party
                analytics platforms. Advertising cookies, if accepted, are used only for
                audience targeting — not to profile your health data.
              </p>
              <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.75, marginBottom: 0 }}>
                You can change your cookie preferences at any time by clearing your browser
                storage or contacting us. For full details, see our{' '}
                <a href="/privacy" style={{ color: C.accent, textDecoration: 'none' }}>Privacy Policy</a>
                {' '}and{' '}
                <a href="/terms" style={{ color: C.accent, textDecoration: 'none' }}>Terms of Service</a>.
              </p>
            </div>
          )}
        </div>

        {/* ── Action buttons ────────────────────────────────────────────── */}
        <div style={{
          padding: '14px 20px',
          borderTop: `1px solid ${C.border}`,
          display: 'flex',
          gap: 10,
          flexShrink: 0,
        }}>
          {/* Reject Non-Essential */}
          <button onClick={rejectAll} style={{
            flex: 1,
            padding: '10px 4px',
            background: 'transparent',
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            color: C.muted,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            lineHeight: 1.3,
          }}>
            Reject Non-Essential
          </button>

          {/* Customize ↔ Save Preferences */}
          {tab === 'details' ? (
            <button onClick={saveCustom} style={{
              flex: 1,
              padding: '10px 4px',
              background: C.card,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              color: C.text,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}>
              Save Preferences
            </button>
          ) : (
            <button onClick={() => setTab('details')} style={{
              flex: 1,
              padding: '10px 4px',
              background: C.card,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              color: C.text,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}>
              Customize
            </button>
          )}

          {/* Accept All */}
          <button onClick={acceptAll} style={{
            flex: 1,
            padding: '10px 4px',
            background: `linear-gradient(135deg, ${C.accent}, ${C.purple})`,
            border: 'none',
            borderRadius: 8,
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
          }}>
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
